import { useEffect, useRef, useState, useCallback } from "react";
import LiveChat from "./LiveChat";
import { SHA256 } from "crypto-js";
import { inboxServices } from "../../APIServices/SitePilotManagement/inboxServices";
import { widgetServices } from "../../APIServices/SitePilotManagement/widgetServices";
import { ChatSocket, roomIdOf } from "../../APIServices/GoServerApi/chatService";
import useAudioCall from "../../APIServices/GoServerApi/useAudioCall";
import useVideoCall from "../../APIServices/GoServerApi/useVideoCall";

const computeDeviceFingerprint = () => {
  const fingerprintString = [
    navigator.userAgent,
    navigator.platform,
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    navigator.language,
    new Date().getTimezoneOffset(),
  ].join("|");

  return SHA256(fingerprintString).toString();
};

const WIDGET_NOTIFY_EVENT = "sitepilot:widget-notify";

const ChatFloatIcon = ({
  widgetId,
  widgetType,
  adminUserId: externalAdminUserId,
  adminName: externalAdminName,
}) => {
  const [showChat, setShowChat] = useState(false);
  const [widgetDetails, setWidgetDetails] = useState("");
  const historySockRef = useRef(null);
  const wsRef = useRef(null);
  const fingerprintRef = useRef("");
  const pendingHistoryRef = useRef([]);

  const [messages, setMessages] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [visitorInfo, setVisitorInfo] = useState("");
  const [visitorType, setVisitorType] = useState("visitor");
  const [visitorData, setVisitorData] = useState(null);
  const [messageLoader, setMessageLoader] = useState(false);
  const [inputMsg, setInputMsg] = useState("");
  const [isTyping, setIsTyping] = useState("");

  const adminUserId =
    externalAdminUserId ||
    widgetDetails?.adminUserId ||
    widgetDetails?.widgetChatSettings?.adminUserId ||
    widgetDetails?.widgetChatSettings?.assignedAdminId ||
    "admin";

  const adminName =
    externalAdminName ||
    widgetDetails?.adminName ||
    widgetDetails?.widgetChatSettings?.adminName ||
    "Admin";

  const getFp = () => {
    if (!fingerprintRef.current) {
      fingerprintRef.current = computeDeviceFingerprint();
    }
    return fingerprintRef.current;
  };

  const callManager = useAudioCall({
    wsRef,
    currentUserId: visitorData?.inboxId || "",
  });

  const videoCallManager = useVideoCall({
    wsRef,
    currentUserId: visitorData?.inboxId || "",
  });

  const audioCallEventHandlerRef = useRef(callManager.handleSocketEvent);
  const videoCallEventHandlerRef = useRef(videoCallManager.handleSocketEvent);

  useEffect(() => {
    audioCallEventHandlerRef.current = callManager.handleSocketEvent;
  }, [callManager.handleSocketEvent]);

  useEffect(() => {
    videoCallEventHandlerRef.current = videoCallManager.handleSocketEvent;
  }, [videoCallManager.handleSocketEvent]);

  const flushPendingHistory = () => {
    const sock = historySockRef.current;
    if (!sock || !sock.isReady()) return;

    const pending = pendingHistoryRef.current || [];
    if (!pending.length) return;

    pending.forEach((payload) => {
      try {
        sock.visitorOpened(payload);
      } catch { }
    });

    pendingHistoryRef.current = [];
  };

  const ensureHistorySocket = () => {
    const fp = getFp();

    if (!historySockRef.current) {
      historySockRef.current = new ChatSocket({
        userId: fp,
        name: fp,
        autoReconnect: true,
        onOpen: () => flushPendingHistory(),
      });
    }

    historySockRef.current.connect();
  };

  const notifyHistoryClick = async () => {
    if (!widgetId) return;

    const payload = {
      fingerprint: getFp(),
      widgetId: widgetId,
      propertyId: propertyId,
      visitorId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      ts: Date.now(),
      userIcon: visitorData?.icon,
      userId: visitorData?.inboxId,
      userName: visitorData?.inboxId,
      pageName: document.title,
      pageURL: window.location.href,
    };

    ensureHistorySocket();
    const sock = historySockRef.current;

    if (sock && sock.isReady()) {
      sock.visitorOpened(payload);
    } else {
      pendingHistoryRef.current = [...(pendingHistoryRef.current || []), payload];
    }
  };

  useEffect(() => {
    return () => {
      try {
        historySockRef.current?.disconnect?.();
      } catch { }
    };
  }, []);

  const sendNotification = useCallback(
    async (type, text) => {
      if (
        !wsRef.current ||
        !visitorData?.inboxId ||
        !visitorData?.sessionId ||
        !propertyId
      ) {
        console.warn("Notification skipped. Widget socket identity is incomplete.", {
          hasSocket: !!wsRef.current,
          propertyId,
          inboxId: visitorData?.inboxId,
          sessionId: visitorData?.sessionId,
          type,
          text,
        });
        return false;
      }

      emitTypingPreview("");

      const notifyData = {
        propertyId,
        sessionId: visitorData?.sessionId,
        visitorId: visitorData?.inboxId,
        type,
        userIcon: visitorData?.icon,
        userId: visitorData?.inboxId,
        userName: visitorData?.name || visitorData?.inboxId,
      };

      if (type === "navigate") {
        notifyData.pageName = document.title;
        notifyData.pageURL = window.location.href;
      }

      if (type === "notification") {
        notifyData.text = text;
      }

      const ok = wsRef.current.sendRoomMessage(notifyData);

      if (!ok) {
        console.error("sendRoomMessage failed for notification", notifyData);
        return false;
      }

      const userData = {
        sessionId: visitorData?.sessionId,
        inboxId: visitorData?.inboxId,
        userIcon: visitorData?.icon,
        userId: visitorData?.inboxId,
        userName: visitorData?.name || visitorData?.inboxId,
        type,
        userType: "visitor",
      };

      if (type === "navigate") {
        userData.pageName = document.title;
        userData.pageURL = window.location.href;
      }

      if (type === "notification") {
        userData.message = text;
      }

      await inboxServices.sendInboxMessage(userData);
      return true;
    },
    [propertyId, visitorData]
  );

  const addNewVisitor = async (propIdArg = propertyId) => {
    if (!widgetId || !propIdArg) return null;

    const userData = {
      widgetId: widgetId,
      deviceFingerprint: getFp(),
      pageName: document.title,
      pageURL: window.location.href,
      inboxId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      userIcon: visitorData?.icon,
      userId: visitorData?.inboxId,
      userName: visitorData?.name || visitorData?.inboxId,
    };

    const data = await inboxServices.addNewVisitor(userData);

    if (data?.statusCode === 200) {
      return await inboxServices.getSingleVisitor(propIdArg, getFp());
    }

    return null;
  };

  const getSingleVisitor = async (propId) => {
    if (!propId) return null;

    let response = await inboxServices.getSingleVisitor(propId, getFp());
    let vData = response?.data || null;
    let nextMessages = response?.messages || [];

    if (!vData) {
      const retryResponse = await addNewVisitor(propId);
      response = retryResponse || response;
      vData = retryResponse?.data || null;
      nextMessages = retryResponse?.messages || [];
    }

    setVisitorType(vData?.type || "visitor");
    setVisitorData(vData || null);
    setMessages(nextMessages);

    return vData;
  };

  const getWidgetChatData = async () => {
    if (!widgetId) return;
    const data = await widgetServices.getWidgetChatData(widgetId, widgetType);
    const propId = data?.data?.propertyId || "";
    setPropertyId(propId);
    setWidgetDetails(data?.data?.widgetSettings || "");
    await getSingleVisitor(propId);
  };

  useEffect(() => {
    if (!widgetId) return;
    getWidgetChatData();
  }, [widgetId, widgetType]);

  const addNewInbox = async () => {
    setMessageLoader(true);

    const payload = {
      inboxId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      visitorInfo: widgetDetails?.widgetChatSettings?.visitorInfo || [],
      userIcon: visitorData?.icon,
      userId: visitorData?.inboxId,
      userName: visitorData?.name || visitorData?.inboxId,
      pageName: document.title,
      pageURL: window.location.href,
      deviceFingerprint: getFp(),
    };

    const data = await inboxServices.addNewInbox(payload);

    if (data?.statusCode === 200) {
      await getWidgetChatData();
    }

    setMessageLoader(false);
  };

  const emitTypingPreview = (text) => {
    const inboxId = visitorData?.inboxId;
    if (!wsRef.current || !propertyId || !inboxId) return;

    wsRef.current.sendRoomTyping({
      propertyId,
      visitorId: inboxId,
      text: text ?? "",
      type: "message",
      userType: "visitor",
      sessionId: visitorData?.sessionId,
      userIcon: visitorData?.icon,
      userId: inboxId,
      userName: visitorData?.name || inboxId,
    });
  };

  const startVisitorAudioCall = async () => {
    if (!callManager) {
      const reason = "Call manager missing";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!wsRef.current) {
      const reason = "Socket missing";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!wsRef.current.isReady?.()) {
      const reason = "Socket not ready";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!propertyId || !visitorData?.inboxId || !visitorData?.sessionId) {
      const reason = "Missing call identity";
      console.error(reason, {
        propertyId,
        inboxId: visitorData?.inboxId,
        sessionId: visitorData?.sessionId,
      });
      return { ok: false, reason };
    }

    const convId = roomIdOf(
      propertyId,
      visitorData?.inboxId,
      visitorData?.sessionId
    );

    if (!convId) {
      const reason = "Invalid conversation id";
      console.error(reason);
      return { ok: false, reason };
    }

    const result = await callManager.startAudioCall({
      convId,
      to: adminUserId,
      propertyId,
      visitorId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      userType: "visitor",
      userId: visitorData?.inboxId,
      userIcon: visitorData?.icon,
      userName: visitorData?.name || visitorData?.inboxId,
      pageName: document.title,
      pageURL: window.location.href,
    });

    if (!result?.ok) {
      console.error("Audio call start failed:", {
        adminUserId,
        convId,
        result,
      });
    }

    return result;
  };

  const startVisitorVideoCall = async () => {
    if (!videoCallManager) {
      const reason = "Video call manager missing";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!wsRef.current) {
      const reason = "Socket missing";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!wsRef.current.isReady?.()) {
      const reason = "Socket not ready";
      console.error(reason);
      return { ok: false, reason };
    }

    if (!propertyId || !visitorData?.inboxId || !visitorData?.sessionId) {
      const reason = "Missing video call identity";
      console.error(reason, {
        propertyId,
        inboxId: visitorData?.inboxId,
        sessionId: visitorData?.sessionId,
      });
      return { ok: false, reason };
    }

    const convId = roomIdOf(
      propertyId,
      visitorData?.inboxId,
      visitorData?.sessionId
    );

    if (!convId) {
      const reason = "Invalid conversation id";
      console.error(reason);
      return { ok: false, reason };
    }

    const result = await videoCallManager.startVideoCall({
      convId,
      to: adminUserId,
      propertyId,
      visitorId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      userType: "visitor",
      userId: visitorData?.inboxId,
      userIcon: visitorData?.icon,
      userName: visitorData?.name || visitorData?.inboxId,
      pageName: document.title,
      pageURL: window.location.href,
    });

    if (!result?.ok) {
      console.error("Video call start failed:", {
        adminUserId,
        convId,
        result,
      });
    }

    return result;
  };

  const initSocket = ({ propId, vData }) => {
    const inboxId = vData?.inboxId;
    const sessionId = vData?.sessionId;
    if (!propId || !inboxId || !sessionId) return null;

    const roomId = roomIdOf(propId, inboxId, sessionId);
    const myName = vData?.name || inboxId;

    const socket = new ChatSocket({
      userId: inboxId,
      name: myName,
      onOpen: () => {
        socket.joinRoom({ propertyId: propId, visitorId: inboxId, sessionId });
      },
      onEvent: (type, data) => {
        audioCallEventHandlerRef.current?.(type, data);
        videoCallEventHandlerRef.current?.(type, data);

        if (data?.type === "message") {
          if (type === "message:new" && data?.convId === roomId) {
            setIsTyping("");
            setMessages((prev) => [
              ...(prev || []),
              {
                messageId: data.id,
                userType: data.userType,
                message: data.text || "",
                pageName: data.pageName || "",
                pageURL: data.pageURL || "",
                type: data.type,
                userIcon: data.userIcon,
                userId: data.userId,
                userName: data.userName,
                createdAt: data.ts || Date.now(),
              },
            ]);
            return;
          }
        }

        if (type === "room:typing" && data?.convId === roomId) {
          const isAdmin =
            data?.from === adminUserId || data?.userType === "admin";
          if (isAdmin) {
            setIsTyping(data?.text ? data : "");
          }
        }
      },
    });

    socket.connect();
    wsRef.current = socket;
    return socket;
  };

  const inboxId = visitorData?.inboxId;
  const sessionId = visitorData?.sessionId;

  useEffect(() => {
    setIsTyping("");

    if (!visitorType || !propertyId || !inboxId || !sessionId) return;

    const socket = initSocket({ propId: propertyId, vData: visitorData });

    return () => {
      try {
        socket?.disconnect?.();
      } catch { }
      wsRef.current = null;
    };
  }, [visitorType, propertyId, inboxId, sessionId, adminUserId]);

  useEffect(() => {
    const handler = async (event) => {
      const detail = event?.detail || {};
      if (!detail?.widgetId || detail.widgetId !== widgetId) return;

      await sendNotification(detail.type, detail.text);
    };

    window.addEventListener(WIDGET_NOTIFY_EVENT, handler);
    return () => window.removeEventListener(WIDGET_NOTIFY_EVENT, handler);
  }, [widgetId, sendNotification]);

  const visitorAddedRef = useRef(false);

  useEffect(() => {
    visitorAddedRef.current = false;
  }, [widgetId]);

  const handleOpenChat = () => {
    setShowChat(true);
    sendNotification("notification", "Visitor Opened the Chat Interface.");
  };

  const updateInboxSession = async () => {
    const userData = {
      inboxId: visitorData?.inboxId,
      sessionId: visitorData?.sessionId,
      chatStatusType: true,
      chatStatus: "closed",
    };

    const data = await inboxServices.updateInboxSession(userData);

    if (data?.statusCode === 200) {
      await getSingleVisitor(propertyId);
      await addNewVisitor(propertyId);
    }
  };

  return (
    <>
      {widgetDetails && (
        <>
          <div
            onClick={handleOpenChat}
            style={{
              position: "fixed",
              bottom: 50,
              right: 24,
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor:
                widgetDetails?.widgetColorSettings?.color || "#000",
              color: "#fff",
              display: showChat ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              zIndex: 2000,
              fontSize: 26,
            }}
          >
            💬
          </div>

          {showChat && (
            <LiveChat
              setShowChat={setShowChat}
              themeColor={widgetDetails?.widgetColorSettings?.color || "#000"}
              messages={messages}
              propertyId={propertyId}
              visitorInfo={widgetDetails?.widgetChatSettings?.visitorInfo || []}
              visitorData={visitorData}
              messageLoader={messageLoader}
              setMessages={setMessages}
              setVisitorInfo={setVisitorInfo}
              addNewInbox={addNewInbox}
              wsRef={wsRef}
              emitTypingPreview={emitTypingPreview}
              visitorType={visitorType}
              inputMsg={inputMsg}
              setInputMsg={setInputMsg}
              isTyping={isTyping}
              widgetDetails={widgetDetails}
              setVisitorType={setVisitorType}
              sendNotification={sendNotification}
              updateInboxSession={updateInboxSession}
              callManager={callManager}
              videoCallManager={videoCallManager}
              onAudioCallClick={startVisitorAudioCall}
              onVideoCallClick={startVisitorVideoCall}
              adminUserId={adminUserId}
              adminName={adminName}
            />
          )}
        </>
      )}
    </>
  );
};

export default ChatFloatIcon;